import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, formatINR, resolveMediaUrl, fetchReissueCount, fetchBundleForProduct } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Heart, ChevronLeft, Plus, Minus, Bell, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import ReviewsSection from "../components/ReviewsSection";
import ReissueModal from "../components/ReissueModal";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [reissueCount, setReissueCount] = useState(0);
  const [bundle, setBundle] = useState(null);
  const { addToCart, toggleWishlist, isWishlisted } = useStore();

  useEffect(() => {
    setActive(0);
    setQty(1);
    fetchProduct(id).then(setProduct).catch(() => setProduct(null));
    fetchReissueCount(id).then((d) => setReissueCount(d.count)).catch(() => setReissueCount(0));
    fetchBundleForProduct(id).then(setBundle).catch(() => setBundle(null));
  }, [id]);

  if (!product) {
    return (
      <main className="bg-[#0A0A0A] min-h-screen pt-[100px] px-6">
        <div className="max-w-[1400px] mx-auto py-32 text-center text-[#A3A3A3] font-serif-display italic text-2xl">
          Loading…
        </div>
      </main>
    );
  }

  const wished = isWishlisted(product.id);

  return (
    <main data-testid="product-detail-page" className="bg-[#0A0A0A] min-h-screen pt-[80px]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37]">
          <ChevronLeft size={14} strokeWidth={1.5} /> Back to Shop
        </Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 pb-24">
        {/* Gallery */}
        <div className="space-y-3">
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="aspect-[4/5] overflow-hidden bg-[#141414] relative select-none"
            onTouchStart={(e) => {
              window._hbTouchX = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              const start = window._hbTouchX;
              if (start == null) return;
              const dx = e.changedTouches[0].clientX - start;
              const len = product.images.length;
              if (len > 1) {
                if (dx > 50) setActive((i) => (i - 1 + len) % len);
                else if (dx < -50) setActive((i) => (i + 1) % len);
              }
              window._hbTouchX = null;
            }}
          >
            <img
              src={resolveMediaUrl(product.images[active])}
              alt={product.name}
              className="w-full h-full object-cover pointer-events-none"
              data-testid="product-main-image"
              draggable={false}
            />
            {product.images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActive((i) => (i - 1 + product.images.length) % product.images.length)}
                  data-testid="pdp-prev"
                  aria-label="Previous"
                  className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-black/40 backdrop-blur text-white hover:bg-[#F5D565] hover:text-[#0A0A0A] transition-colors"
                >
                  <ChevronLeft size={18} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setActive((i) => (i + 1) % product.images.length)}
                  data-testid="pdp-next"
                  aria-label="Next"
                  className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-black/40 backdrop-blur text-white hover:bg-[#F5D565] hover:text-[#0A0A0A] transition-colors"
                >
                  <ChevronLeft size={18} strokeWidth={1.5} className="rotate-180" />
                </button>
                {/* Dots indicator (mobile cue for swipe) */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 md:hidden">
                  {product.images.map((_, i) => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full transition-all"
                      style={{ background: i === active ? "#F5D565" : "rgba(255,255,255,0.4)" }}
                    />
                  ))}
                </div>
              </>
            )}
          </motion.div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`aspect-square overflow-hidden border ${
                    i === active ? "border-[#F5D565]" : "border-[#262626]"
                  } hover:border-[#F5D565] transition-colors`}
                >
                  <img src={resolveMediaUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:pl-6">
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">
            {product.category}
          </div>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-[0.95]">
            {product.name}
          </h1>
          <div className="mt-4 flex items-baseline gap-3" data-testid="product-price">
            <span className="font-body text-2xl text-[#F5F5F5]">{formatINR(product.price)}</span>
            {(() => {
              const mrp = Number(product.mrp) || 0;
              const price = Number(product.price) || 0;
              if (mrp > price && price > 0) {
                const save = mrp - price;
                const pct = Math.round((save / mrp) * 100);
                return (
                  <>
                    <span
                      data-testid="product-mrp"
                      className="font-body text-lg text-[#8b8b8b] line-through"
                    >
                      {formatINR(mrp)}
                    </span>
                    <span
                      data-testid="product-savings-badge"
                      className="font-ui"
                      style={{
                        fontSize: 11, fontWeight: 700, color: "#F5D565",
                        letterSpacing: "0.18em", textTransform: "uppercase",
                        border: "1px solid #F5D565", padding: "4px 10px",
                      }}
                    >
                      {pct}% OFF
                    </span>
                  </>
                );
              }
              return null;
            })()}
          </div>
          {(() => {
            const mrp = Number(product.mrp) || 0;
            const price = Number(product.price) || 0;
            if (mrp > price && price > 0) {
              const save = mrp - price;
              const pct = Math.round((save / mrp) * 100);
              return (
                <div
                  data-testid="product-savings-line"
                  className="mt-1 font-body text-[13px]"
                  style={{ color: "#F5D565" }}
                >
                  You save {formatINR(save)} ({pct}% OFF)
                </div>
              );
            }
            return null;
          })()}

          <p className="mt-6 text-[#A3A3A3] leading-relaxed text-sm max-w-md">
            {product.description}
          </p>

          {/* Singular Piece label / Sold Out badge under price */}
          {(() => {
            const stock = typeof product.stock_quantity === "number" ? product.stock_quantity : 1;
            const isSingular = product.is_singular !== false;
            const sold = stock <= 0;
            if (sold) {
              return (
                <div className="mt-4">
                  <div
                    data-testid="pdp-sold-out-badge"
                    className="inline-block font-serif-display italic"
                    style={{
                      background: "#F5E9CE", color: "#8B6E2C",
                      padding: "12px 32px", fontSize: 28, letterSpacing: "0.05em",
                    }}
                  >
                    Sold Out
                  </div>
                  <div
                    data-testid="pdp-unavailable-label"
                    className="mt-3 font-ui"
                    style={{ fontSize: 11, color: "#F5D565", letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600 }}
                  >
                    Currently Unavailable
                    {reissueCount > 0 && (
                      <span className="opacity-70 ml-2" data-testid="pdp-reissue-count">· {reissueCount} on waitlist</span>
                    )}
                  </div>
                  <p className="mt-2 font-body text-[13px]" style={{ color: "rgba(255,255,255,0.7)" }}>
                    Join the waitlist for restock updates.
                  </p>
                </div>
              );
            }
            if (isSingular) {
              return null;
            }
            if (stock > 1) {
              return (
                <div data-testid="pdp-stock-label" className="mt-3 font-ui"
                  style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600 }}>
                  {stock} in stock
                </div>
              );
            }
            return null;
          })()}

          {product.collections?.length > 0 && (
            <div className="mt-6 flex gap-2 flex-wrap">
              {product.collections.map((c) => (
                <span key={c} className="pill text-xs">{c}</span>
              ))}
            </div>
          )}

          {/* Qty — hidden for singular pieces (limited to 1) and sold-out */}
          {(() => {
            const stock = typeof product.stock_quantity === "number" ? product.stock_quantity : 1;
            const isSingular = product.is_singular !== false;
            if (stock <= 0 || isSingular) return null;
            return (
              <div className="mt-10 flex items-center gap-4">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Qty</span>
                <div className="inline-flex items-center border border-[#262626]">
                  <button
                    data-testid="pdp-qty-decrement"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:text-[#D4AF37]"
                  >
                    <Minus size={14} strokeWidth={1.5} />
                  </button>
                  <span className="w-10 text-center text-sm">{qty}</span>
                  <button
                    data-testid="pdp-qty-increment"
                    onClick={() => setQty(Math.min(stock, qty + 1))}
                    className="w-10 h-10 flex items-center justify-center hover:text-[#D4AF37]"
                  >
                    <Plus size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Bundle Offer box (above CTA) */}
          {bundle && Array.isArray(bundle.tiers) && bundle.tiers.length > 0 && (
            <div
              data-testid="pdp-bundle-offer"
              className="mt-6 border p-4"
              style={{ borderColor: "#F5D565", background: "rgba(245,213,101,0.06)" }}
            >
              <div className="flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.75} style={{ color: "#F5D565" }} />
                <span className="font-ui" style={{ fontSize: 11, color: "#F5D565", letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 700 }}>
                  HER BOX Bundle Offer
                </span>
              </div>
              <div className="font-serif-display mt-2" style={{ fontSize: 15, color: "#FFFFFF" }}>
                {bundle.name}
              </div>
              <div className="mt-3 space-y-1.5">
                {bundle.tiers.map((t, i) => {
                  const isBest = i === Math.min(1, bundle.tiers.length - 1);
                  return (
                    <div key={i} className="flex items-center justify-between font-body" style={{ fontSize: 13, color: "rgba(255,255,255,0.9)" }}>
                      <span>{t.qty} {t.qty === 1 ? "piece" : "pieces"} · {formatINR(t.price)}</span>
                      {isBest && bundle.tiers.length > 1 && (
                        <span className="font-ui" style={{ fontSize: 9, color: "#0A0A0A", background: "#F5D565", padding: "3px 8px", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>
                          Most Popular
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 font-body text-[11px]" style={{ color: "rgba(255,255,255,0.5)" }}>
                Auto-applied when your cart has qualifying pieces from <em>{bundle.collection}</em>.
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            {(() => {
              const stock = typeof product.stock_quantity === "number" ? product.stock_quantity : 1;
              if (stock <= 0) {
                return (
                  <button
                    data-testid="pdp-notify-me"
                    className="btn-gold flex-1 min-w-[200px] inline-flex items-center justify-center gap-2"
                    onClick={() => setReissueOpen(true)}
                  >
                    <Bell size={16} strokeWidth={1.75} /> Notify When Available
                  </button>
                );
              }
              return (
                <button
                  data-testid="pdp-add-to-cart"
                  className="btn-gold flex-1 min-w-[200px]"
                  onClick={() => addToCart(product, qty)}
                >
                  Add to Box · {formatINR(product.price * qty)}
                </button>
              );
            })()}
            <button
              data-testid="pdp-wishlist-toggle"
              onClick={() => toggleWishlist(product.id)}
              className={`btn-ghost ${wished ? "bg-[#F5D565] text-[#0A0A0A] border-[#F5D565]" : ""}`}
              aria-label="Wishlist"
            >
              <Heart size={16} strokeWidth={1.5} fill={wished ? "currentColor" : "none"} />
            </button>
          </div>

          {product.price < 3000 && (
            <div
              data-testid="cod-badge"
              className="mt-5 inline-flex items-center gap-2 font-ui"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#F5D565",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                border: "1px solid #F5D565",
                padding: "8px 14px",
                borderRadius: 9999,
              }}
            >
              ✓ COD Available
            </div>
          )}

          <div className="mt-10 pt-8 border-t border-[#262626] grid grid-cols-2 gap-6 font-body" style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
            <div>
              <div style={{ color: "#F5D565", marginBottom: 8, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>✦ Delivery</div>
              <span data-testid="pdp-delivery-timeline">5-7 working days</span>
            </div>
            <div>
              <div style={{ color: "#F5D565", marginBottom: 8, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>✦ Edge Box</div>
              Signature gift box included
            </div>
            <div>
              <div style={{ color: "#F5D565", marginBottom: 8, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>✦ Wear</div>
              Skin friendly
            </div>
            <div>
              <div style={{ color: "#F5D565", marginBottom: 8, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>✦ COD</div>
              Available across India
            </div>
          </div>

          {/* "The Last One" — bottom of product display, only for singular in-stock pieces */}
          {(() => {
            const stock = typeof product.stock_quantity === "number" ? product.stock_quantity : 1;
            const isSingular = product.is_singular !== false;
            if (isSingular && stock > 0) {
              return (
                <div
                  data-testid="pdp-last-one-badge"
                  className="mt-8 pt-6 border-t border-[#262626] text-center"
                >
                  <div className="font-serif-display italic" style={{ fontSize: 26, color: "#F5D565", letterSpacing: "0.02em" }}>
                    The Last One
                  </div>
                  <div className="font-ui mt-2" style={{ fontSize: 10, letterSpacing: "0.35em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>
                    Once it&apos;s gone, it&apos;s gone.
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>
      <ReviewsSection productId={product.id} compact />
      <ReissueModal
        product={product}
        open={reissueOpen}
        onClose={() => setReissueOpen(false)}
        onSubmitted={() => fetchReissueCount(product.id).then((d) => setReissueCount(d.count)).catch(() => {})}
      />
    </main>
  );
}
